import { destroy, getWithConfig, postCustomize, put } from "../config/api";
import { getClient, getToken, getUid } from "./AuthService";

export type PeriodStatus = "draft" | "published";

export interface TransparencyPeriodSummary {
  id: number;
  month: number;
  year: number;
  status: PeriodStatus;
  attorneysCount: number;
}

export interface AttorneyFees {
  gross: number | null;
  otherDeductions: number | null;
  incomeTax: number | null;
  net: number | null;
}

export interface AttorneyAids {
  health: number | null;
  meal: number | null;
}

export interface TransparencyAttorney {
  id: number;
  name: string;
  jobTitle: string | null;
  fees: AttorneyFees;
  aids: AttorneyAids;
}

export interface TransparencyPeriodDetail {
  id: number;
  month: number;
  year: number;
  status: PeriodStatus;
  feesNote: string | null;
  aidsNote: string | null;
  attorneys: TransparencyAttorney[];
}

export type IgnoredReason = "missing_registration" | "duplicated";

export interface IgnoredRow {
  name: string;
  registration: string | null;
  reason: IgnoredReason;
}

export interface SpreadsheetReport {
  attorneysCount: number;
  ignored: IgnoredRow[];
  feesNote: string | null;
  aidsNote: string | null;
}

export interface SpreadsheetError {
  code: string;
  column?: string;
  line?: number;
  name?: string;
  registration?: string;
}

export interface SpreadsheetForm {
  month: number;
  year: number;
  file: File;
}

export interface PublishConflict {
  error: "already_published";
  current: Pick<TransparencyPeriodSummary, "id" | "month" | "year" | "status">;
}

const authHeaders = () => ({
  "Access-Token": getToken(),
  client: getClient(),
  Uid: getUid(),
});

export default class AdminTransparencyService {
  public static async periods() {
    return getWithConfig<TransparencyPeriodSummary[]>("/admin/transparencia/planilhas", { headers: authHeaders() })
      .then((response) => response.data);
  }

  public static async period(id: string | number) {
    return getWithConfig<TransparencyPeriodDetail>(`/admin/transparencia/planilhas/${id}`, { headers: authHeaders() })
      .then((response) => response.data);
  }

  public static async validate(form: SpreadsheetForm) {
    return postCustomize<SpreadsheetReport>("/admin/transparencia/planilhas/validacao", formData(form), { headers: authHeaders() })
      .then((response) => response.data);
  }

  public static async create(form: SpreadsheetForm) {
    return postCustomize<TransparencyPeriodSummary>("/admin/transparencia/planilhas", formData(form), { headers: authHeaders() })
      .then((response) => response.data);
  }

  public static async updateNotes(id: number, feesNote: string, aidsNote: string) {
    return put<TransparencyPeriodDetail>(`/admin/transparencia/planilhas/${id}`, { feesNote, aidsNote }, { headers: authHeaders() })
      .then((response) => response.data);
  }

  public static async publish(id: number, replace = false) {
    return postCustomize<TransparencyPeriodSummary>(`/admin/transparencia/planilhas/${id}/publicacao`, { replace }, { headers: authHeaders() })
      .then((response) => response.data);
  }

  public static async unpublish(id: number) {
    return destroy<TransparencyPeriodSummary>(`/admin/transparencia/planilhas/${id}/publicacao`, { headers: authHeaders() })
      .then((response) => response.data);
  }
}

function formData({ month, year, file }: SpreadsheetForm) {
  const data = new FormData();
  data.append("month", String(month));
  data.append("year", String(year));
  data.append("file", file);
  return data;
}

export function spreadsheetErrors(error: unknown): SpreadsheetError[] | null {
  const errors = typeof error === "object" && error !== null ? (error as { errors?: unknown }).errors : undefined;
  return Array.isArray(errors) ? (errors as SpreadsheetError[]) : null;
}

export function isPublishConflict(error: unknown): error is PublishConflict {
  return typeof error === "object" && error !== null && (error as { error?: string }).error === "already_published";
}
